import type { Client } from './interfaces';
import type { ProviderActivityDocument } from './ProviderActivityApi';

// Only events emitted by mobile-app use qualify; Auth timestamps also include dashboard logins.
const MOBILE_APP_USAGE_EVENTS = new Set([
    'user_signed_in',
    'onboarding_started',
    'onboarding_completed',
    'completed_onboarding',
    'goal_completed',
    'app_session',
]);

// Legacy clients can be referenced by either their document ID or their linked Auth user ID.
export function getClientActivityIdentifiers(client: Client): string[] {
    return Array.from(new Set([client.id, client.userId].filter(
        (identifier): identifier is string => typeof identifier === 'string' && identifier.length > 0
    )));
}

// Preserve all-time app-use evidence independently of chart dates and onboarding stages.
export function getMobileAppUsageByClientId(
    clients: Client[],
    activityDocuments: ProviderActivityDocument[]
): Record<string, boolean> {
    const appUserIds = new Set(activityDocuments
        .filter(({ data }) => MOBILE_APP_USAGE_EVENTS.has(data.type))
        .map(({ data }) => data.userId));
    return Object.fromEntries(clients
        .filter((client) => client.id && getClientActivityIdentifiers(client).some((id) => appUserIds.has(id)))
        .map((client) => [client.id as string, true]));
}

// Count each care recipient once when any linked carer has actually used the mobile app.
export function countFamiliesUsingApp(
    clients: Client[],
    mobileAppUsageByClientId: Record<string, boolean>
): number {
    const activeCarerIds = new Set(clients
        .filter((client) => getClientActivityIdentifiers(client).some((id) => mobileAppUsageByClientId[id] === true))
        .flatMap(getClientActivityIdentifiers));
    const careRecipientIds = new Set(clients
        .filter((client) => client.id && Array.isArray(client.carers) &&
            client.carers.some((carerId) => activeCarerIds.has(carerId)))
        .map((client) => client.id));
    return careRecipientIds.size;
}
